// Retrouve la planche d'un contrat même quand boardId est vide
// (les réservations synchronisées depuis Google Sheets n'ont que boardName/serial).

export function matchBoard(contract, boards) {
  if (!contract || !boards || !boards.length) return null

  // 1. boardId direct
  if (contract.boardId) {
    const b = boards.find(b => b.id === contract.boardId)
    if (b) return b
  }

  const name = String(contract.boardName || contract.boardSize || '').trim()

  // 2. Nom exact (ex : "Planche 1")
  if (name) {
    const b = boards.find(b => b.name === name)
    if (b) return b
  }

  // 3. Numéro de série
  if (contract.serial) {
    const b = boards.find(b => b.serial && b.serial === contract.serial)
    if (b) return b
  }

  // 4. Modèle + #numéro — match par fragment de taille extrait du boardName
  const m = name.match(/#\s*(\d+)/)
  if (m) {
    const num = parseInt(m[1], 10)
    const sizes = name.match(/\d+'\d*"?/g) || []
    const pool = boards.filter(b => {
      const bm = (b.model || '').toLowerCase()
      return sizes.length ? sizes.some(s => bm.includes(s.toLowerCase())) : true
    })
    if (pool[num - 1]) return pool[num - 1]
  }

  return null
}

export function matchBoardId(contract, boards) {
  const b = matchBoard(contract, boards)
  return b ? b.id : null
}

// Nombre de planches par modèle demandées par une réservation.
// Retourne { modeleId: qty } — générique, fonctionne avec N modèles.
// Source PRIORITAIRE : la colonne structurée `lignes` = [{modele:'10p6', qty:2}, ...].
// Repli : parsing du texte boardSize/boardName (reconnaît les fragments de taille).
export function parseModelCounts(c) {
  // 1) Stockage structuré (fiable)
  if (Array.isArray(c.lignes) && c.lignes.length) {
    const map = {}
    c.lignes.forEach(l => {
      const n = Number(l?.qty) || 0
      if (l?.modele && n > 0) map[l.modele] = (map[l.modele] || 0) + n
    })
    if (Object.keys(map).length) return map
  }

  // 2) Repli texte — on compte chaque segment séparé par + ou ,
  const s = String(c.boardSize || c.boardName || '')
  const map = {}
  s.split(/[+,]/).forEach(seg => {
    const qm = seg.match(/(\d+)\s*×/)
    const n = qm ? parseInt(qm[1], 10) : 1
    // Extrait le premier fragment de taille "X'Y\"" du segment comme clé approx.
    const size = (seg.match(/\d+'\d*"?/) || [])[0]
    if (size) map[size] = (map[size] || 0) + n
  })
  if (Object.keys(map).length) return map

  // 3) Dernier repli : 1 planche "principale" (premier modèle)
  return { _default: 1 }
}

// ───────── Helpers GÉNÉRIQUES pilotés par la table `modeles` ─────────

// Identifie l'id canonique d'un modèle à partir du texte libre `board.model`,
// en s'appuyant sur la table modeles (label puis nom_complet puis fragment de taille).
export function boardModeleId(board, modeles = []) {
  const txt = String(board?.model || '').toLowerCase()
  if (!txt) return null

  for (const m of modeles) {
    const label = String(m.label || '').toLowerCase()
    if (label && txt.includes(label)) return m.id
  }
  for (const m of modeles) {
    const nom = String(m.nom_complet || '').toLowerCase()
    if (nom && (txt.includes(nom) || nom.includes(txt))) return m.id
  }
  return null
}

// Libellé clair d'une planche : « Bluefin 10'8" n°1 » au lieu de « Planche 1 »
// (le numéro seul est ambigu : Malibu et Waves sont toutes deux numérotées à partir de 1).
export function boardLabel(planche, modeles = []) {
  if (!planche) return '—'
  const m = modeles.find(x => x.id === planche.modele)
  const short = m
    ? (String(m.nom_complet || '').replace(/^Bluefin\s+/i, '').trim() || m.label || planche.modele)
    : (planche.modele || '')
  const num = planche.numero ?? ''
  return short ? `${short} n°${num}` : `Planche ${num}`
}

// Parc de planches par modèle → { '10p6': 8, '9p5': 2, … }.
export function fleetByModele(boards = [], modeles = []) {
  const out = {}
  modeles.forEach(m => { out[m.id] = 0 })
  boards.forEach(b => {
    const id = boardModeleId(b, modeles)
    if (id != null) out[id] = (out[id] || 0) + 1
  })
  return out
}

// Quantités demandées par une réservation, par modèle → { '10p6': 2, '9p5': 1 }.
// Source prioritaire : `lignes` structuré ; repli : parseModelCounts.
export function countByModele(c) {
  if (Array.isArray(c.lignes) && c.lignes.length) {
    const map = {}
    c.lignes.forEach(l => {
      if (l && l.modele) map[l.modele] = (map[l.modele] || 0) + (Number(l.qty) || 0)
    })
    if (Object.keys(map).length) return map
  }
  return parseModelCounts(c)
}

// Résumé planche LISIBLE d'une réservation depuis `lignes` (mode formules) :
// "1× Bluefin 10'8", 1× Bluefin 12'". Repli universel quand board_size/board_name sont vides,
// quel que soit le chemin de création (tunnel formules, webhook, admin).
export function boardSummary(c, modeles = []) {
  const counts = countByModele(c)
  const parts = Object.entries(counts)
    .filter(([id, q]) => id !== '_default' && Number(q) > 0)
    .map(([id, q]) => {
      const m = modeles.find(x => x.id === id)
      const name = m ? (String(m.nom_complet || '').replace(/^Bluefin\s+/i, '').trim() || m.label || id) : id
      return `${q}× ${name}`
    })
  return parts.join(', ')
}

// Affecte les réservations d'une semaine aux planches physiques du bon modèle.
// GÉNÉRIQUE : utilise les IDs de modèles issus de la table `modeles`, pas des strings hardcodés.
// Retourne un objet { boardId: contrat }.
export function assignWeek(week, contracts, boards, modeles = []) {
  const map = {}
  const ACTIVE = c => c.status !== 'cancelled' && c.status !== 'demande' && c.status !== 'rejetee'
  const overlapping = contracts.filter(c =>
    c.startDate && c.endDate &&
    String(c.startDate).split('T')[0] < week.end &&
    String(c.endDate).split('T')[0] > week.start &&
    ACTIVE(c)
  )
  const used = new Set()

  // 1) réservations déjà affectées à une planche précise (admin)
  overlapping.forEach(c => {
    if (c.boardId && boards.some(b => b.id === c.boardId) && !map[c.boardId]) {
      map[c.boardId] = c; used.add(c.boardId)
    }
  })

  // 2) Pool de planches libres par modèle (générique, basé sur modeles.id)
  const freePools = {}
  modeles.forEach(m => {
    freePools[m.id] = boards
      .filter(b => boardModeleId(b, modeles) === m.id && !used.has(b.id))
      .map(b => b.id)
  })
  const cursors = {}
  modeles.forEach(m => { cursors[m.id] = 0 })

  // 3) Distribution par modèle pour les réservations sans planche précise
  overlapping.forEach(c => {
    const qtys = countByModele(c)

    // Déduire la planche déjà affectée (étape 1)
    if (c.boardId && map[c.boardId] === c) {
      const alreadyId = boardModeleId(boards.find(b => b.id === c.boardId) || {}, modeles)
      if (alreadyId && qtys[alreadyId] > 0) qtys[alreadyId]--
    }

    Object.entries(qtys).forEach(([modeleId, qty]) => {
      const pool = freePools[modeleId]
      if (!pool) return
      let remaining = qty
      while (remaining-- > 0 && cursors[modeleId] < pool.length) {
        map[pool[cursors[modeleId]++]] = c
      }
    })
  })

  return map
}
